/**
 * invoiceViewModel.js
 *
 * Single source of truth for all invoice rendering data.
 *
 * Two factory functions accept the raw data shapes that exist in the app:
 *   - buildFromFormState  → called while creating/editing (percentages in form)
 *   - buildFromApiResponse → called after fetching a saved invoice (absolute ₹ amounts from DB)
 *
 * Both return the same canonical InvoiceDisplayModel so that every layout
 * component receives pre-computed, consistent values with NO arithmetic of its own.
 *
 * Adding a new field to the model: add it here once; all layouts pick it up automatically.
 */

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Round a number to 2 decimal places.
 * @param {number} n
 * @returns {number}
 */
const r2 = (n) => +Number(n || 0).toFixed(2);

/**
 * Compute item row totals (qty × price).
 * @param {Array} items
 * @returns {Array}
 */
const computeItems = (items = []) =>
  items.map((item) => ({
    ...item,
    quantity: Number(item.quantity || 0),
    price: r2(item.price),
    amount: r2(Number(item.quantity || 0) * Number(item.price || 0)),
  }));

// ---------------------------------------------------------------------------
// Canonical model builder (private — both factories call this)
// ---------------------------------------------------------------------------

/**
 * Assembles the canonical InvoiceDisplayModel from already-resolved values.
 *
 * @param {object} p
 * @param {object}   p.invoiceMeta     — raw invoice fields (dates, notes, status, etc.)
 * @param {Array}    p.items           — already-computed item rows
 * @param {number}   p.subtotal        — sum of item amounts
 * @param {number}   p.discountPercent — discount as a percentage (0–100)
 * @param {number}   p.discountAmount  — discount in ₹
 * @param {number}   p.taxableAmount   — subtotal − discountAmount
 * @param {number}   p.cgstPercent
 * @param {number}   p.cgstAmount      — ₹
 * @param {number}   p.sgstPercent
 * @param {number}   p.sgstAmount      — ₹
 * @param {number}   p.igstPercent
 * @param {number}   p.igstAmount      — ₹
 * @param {number}   p.shipping
 * @param {number}   p.roundoff
 * @param {number}   p.total
 * @param {object}   p.organization
 * @param {object}   p.customer
 * @param {object}   p.customerShipped
 * @param {object}   p.template
 * @param {string}   p.copyType
 * @returns {object} InvoiceDisplayModel
 */
const buildModel = ({
  invoiceMeta,
  items,
  subtotal,
  discountPercent,
  discountAmount,
  taxableAmount,
  cgstPercent,
  cgstAmount,
  sgstPercent,
  sgstAmount,
  igstPercent,
  igstAmount,
  shipping,
  roundoff,
  total,
  organization,
  customer,
  customerShipped,
  template,
  copyType,
}) => ({
  // ── Invoice metadata ──────────────────────────────────────────────────────
  invoice_number: invoiceMeta?.invoice_number || "To be generated",
  invoice_date: invoiceMeta?.invoice_date || "",
  due_date: invoiceMeta?.due_date || "",
  status: invoiceMeta?.status || "pending",
  reverse_charge: !!invoiceMeta?.reverse_charge,
  transport: invoiceMeta?.transport || "",
  e_way_bill_no: invoiceMeta?.e_way_bill_no || "",
  irn_number: invoiceMeta?.irn_number || "",
  notes: invoiceMeta?.notes || "",
  terms: invoiceMeta?.terms || "",
  copyType: copyType || "original",

  // ── Items (always fully computed) ─────────────────────────────────────────
  items,

  // ── Financials — ALL in absolute ₹ amounts ────────────────────────────────
  subtotal: r2(subtotal),
  discountPercent: r2(discountPercent),
  discountAmount: r2(discountAmount),
  taxableAmount: r2(taxableAmount),
  cgstPercent: r2(cgstPercent),
  cgstAmount: r2(cgstAmount),
  sgstPercent: r2(sgstPercent),
  sgstAmount: r2(sgstAmount),
  igstPercent: r2(igstPercent),
  igstAmount: r2(igstAmount),
  shipping: r2(shipping),
  roundoff: r2(roundoff),
  total: r2(total),

  // ── Related entities (already resolved) ───────────────────────────────────
  organization: organization || null,
  customer: customer || null,
  customerShipped: customerShipped || null,
  template: template || null,
});

// ---------------------------------------------------------------------------
// Public factory: from FORM STATE (editor — taxes are percentages)
// ---------------------------------------------------------------------------

/**
 * Build the canonical model from the invoice editor's form state.
 *
 * In the editor, invoice.cgst / invoice.sgst / invoice.igst / invoice.discount
 * are all PERCENTAGE values (e.g., 9 for 9%).
 *
 * The `totals` memo from InvoiceEditorPage is expected to contain:
 *   { rows, subtotal, discountPercent, discount (abs), shipping, cgst (abs),
 *     sgst (abs), igst (abs), roundoff, total }
 *
 * @param {object} p
 * @param {object} p.invoice           — raw form state
 * @param {object} p.totals            — computed totals memo from the editor
 * @param {object} p.organization
 * @param {object} p.customer
 * @param {object} p.customerShipped
 * @param {object} p.template
 * @param {string} [p.copyType]
 * @returns {object} InvoiceDisplayModel
 */
export const buildFromFormState = ({
  invoice,
  totals,
  organization,
  customer,
  customerShipped,
  template,
  copyType = "original",
}) => {
  const items = totals?.rows || computeItems(invoice?.items);
  const subtotal = r2(totals?.subtotal ?? 0);
  const discountPercent = r2(Number(invoice?.discount || 0));
  const discountAmount = r2(totals?.discount ?? subtotal * (discountPercent / 100));
  const taxableAmount = r2(subtotal - discountAmount);
  const cgstPercent = r2(Number(invoice?.cgst || 0));
  const cgstAmount = r2(totals?.cgst ?? taxableAmount * (cgstPercent / 100));
  const sgstPercent = r2(Number(invoice?.sgst || 0));
  const sgstAmount = r2(totals?.sgst ?? taxableAmount * (sgstPercent / 100));
  const igstPercent = r2(Number(invoice?.igst || 0));
  const igstAmount = r2(totals?.igst ?? taxableAmount * (igstPercent / 100));
  const shipping = r2(totals?.shipping ?? Number(invoice?.shipping || 0));
  const roundoff = r2(totals?.roundoff ?? 0);
  const total = r2(totals?.total ?? 0);

  return buildModel({
    invoiceMeta: invoice,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    taxableAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
    copyType,
  });
};

// ---------------------------------------------------------------------------
// Public factory: from API RESPONSE (saved invoice — taxes are absolute ₹)
// ---------------------------------------------------------------------------

/**
 * Build the canonical model from an API-fetched invoice.
 *
 * In the API response, invoice.discount / invoice.cgst / invoice.sgst /
 * invoice.igst are ABSOLUTE RUPEE AMOUNTS stored by the backend.
 *
 * @param {object} p
 * @param {object} p.invoice           — raw API invoice object
 * @param {object} p.organization
 * @param {object} p.customer
 * @param {object} p.customerShipped
 * @param {object} p.template
 * @param {string} [p.copyType]
 * @returns {object} InvoiceDisplayModel
 */
export const buildFromApiResponse = ({
  invoice,
  organization,
  customer,
  customerShipped,
  template,
  copyType = "original",
}) => {
  const items = computeItems(invoice?.items);
  const subtotal = r2(invoice?.subtotal || 0);
  // Backend stores discount as absolute ₹ amount
  const discountAmount = r2(invoice?.discount || 0);
  const discountPercent = subtotal > 0 ? r2((discountAmount / subtotal) * 100) : 0;
  const taxableAmount = r2(subtotal - discountAmount);
  // Backend stores cgst/sgst/igst as absolute ₹ amounts
  const cgstAmount = r2(invoice?.cgst || 0);
  const cgstPercent = taxableAmount > 0 ? r2((cgstAmount / taxableAmount) * 100) : 0;
  const sgstAmount = r2(invoice?.sgst || 0);
  const sgstPercent = taxableAmount > 0 ? r2((sgstAmount / taxableAmount) * 100) : 0;
  const igstAmount = r2(invoice?.igst || 0);
  const igstPercent = taxableAmount > 0 ? r2((igstAmount / taxableAmount) * 100) : 0;
  const shipping = r2(invoice?.shipping || 0);
  const roundoff = r2(invoice?.roundoff || 0);
  const total = r2(invoice?.total || 0);

  return buildModel({
    invoiceMeta: invoice,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    taxableAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
    copyType,
  });
};

/**
 * Build the canonical model from a static/hardcoded preview invoice object.
 * Used by TemplateBuilderPage where invoice data is already in the canonical shape
 * (absolute amounts, not percentages).
 *
 * @param {object} p
 * @param {object} p.invoice           — static preview invoice (amounts already absolute)
 * @param {object} p.organization
 * @param {object} p.customer
 * @param {object} p.template
 * @param {string} [p.copyType]
 * @returns {object} InvoiceDisplayModel
 */
export const buildFromStaticPreview = ({
  invoice,
  organization,
  customer,
  template,
  copyType = "original",
}) =>
  buildFromApiResponse({
    invoice,
    organization,
    customer,
    customerShipped: null,
    template,
    copyType,
  });
